type ConsoleLogLevel = 'log' | 'warn' | 'error' | 'debug' | 'info';

// Store original console methods to preserve functionality
const originalConsoleMethods: Record<ConsoleLogLevel, typeof console.log> = {
  log: console.log,
  warn: console.warn,
  error: console.error,
  debug: console.debug,
  info: console.info,
};

/**
 * Export original console methods for use by MainProcessLogService
 * to avoid double-prefixing main process logs.
 */
export { originalConsoleMethods };

/**
 * Check if running in Electron environment.
 */
function isRunningInElectron(): boolean {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return typeof window !== 'undefined' && (window as any).electron?.versions?.electron !== undefined;
}

/**
 * Returns CSS style for the timestamp prefix based on log level.
 */
function getLogStyle(level: ConsoleLogLevel): string {
  const baseStyle = 'font-weight: bold; padding: 2px 4px; border-radius: 2px;';

  switch (level) {
    case 'error':
      return `${baseStyle} background: #cc0000; color: white;`;
    case 'warn':
      return `${baseStyle} background: #cc8800; color: black;`;
    case 'debug':
      return `${baseStyle} background: #6622cc; color: white;`;
    case 'info':
      return `${baseStyle} background: #2266cc; color: white;`;
    default: // 'log'
      return `${baseStyle} background: #228822; color: white;`;
  }
}

/**
 * Initializes the console interceptor for renderer process logging.
 * Adds ISO 8601 timestamps to all console output.
 * Only active in development mode.
 *
 * When running in Electron, adds [RENDERER timestamp] prefix to distinguish
 * from main process logs. When running in browser, just adds [timestamp].
 */
export function initRendererConsoleInterceptor(): void {
  // TODO: Re-enable production check after testing
  // if (environment.production) {
  //   return;
  // }

  // Only add "RENDERER" prefix when in Electron (to distinguish from MAIN logs)
  const inElectron = isRunningInElectron();

  const levels: ConsoleLogLevel[] = ['log', 'warn', 'error', 'debug', 'info'];

  levels.forEach((level) => {
    const originalMethod = originalConsoleMethods[level];

    // Override the console method
    console[level] = (...args: unknown[]) => {
      const timestamp = new Date().toISOString();
      // In Electron: [RENDERER timestamp], in browser: just [timestamp]
      const prefix = inElectron ? `[RENDERER ${timestamp}]` : `[${timestamp}]`;
      const style = getLogStyle(level);

      // Call original method with styled timestamp prefix
      if (args.length > 0) {
        originalMethod.call(console, `%c${prefix}`, style, ...args);
      } else {
        originalMethod.call(console, `%c${prefix}`, style);
      }
    };
  });

  // Log that interception is active (using the new format)
  const modeLabel = inElectron ? 'Renderer' : 'Browser';
  console.log(`${modeLabel} console logging with timestamps enabled for development`);
}

/**
 * Restores original console methods.
 * Useful for cleanup or testing.
 */
export function restoreOriginalConsole(): void {
  const levels: ConsoleLogLevel[] = ['log', 'warn', 'error', 'debug', 'info'];

  levels.forEach((level) => {
    console[level] = originalConsoleMethods[level];
  });
}
